import { useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'
import * as THREE from 'three'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { collectContour } from './contour'
import { getOutlineMaterial } from './materials'
import { useRenderStyle } from './style'
import { useViewStore } from '../state/viewStore'

function createOutlineMaterial(color: string, width: number): LineMaterial {
  const material = new LineMaterial({
    color: new THREE.Color(color).getHex(),
    linewidth: width,
    worldUnits: false,
  })
  material.name = `outline:${color}:${width}`
  return material
}

/** Scratch buffer reused across every rebuild so the GC stays quiet. */
const scratch: number[] = []

export interface OutlinedProps {
  children: ReactNode
  /** Overrides the theme contour colour — used for selection and placement ghosts. */
  color?: string
  width?: number
  opacity?: number
  /** Skips the contour pass entirely. */
  disabled?: boolean
  /**
   * Hides the fills while keeping the contour — the placement ghost. The meshes
   * stay in the graph (and keep their own `visible`), so silhouettes still
   * resolve correctly.
   */
  contentVisible?: boolean
}

/**
 * Folds the entire contour of an asset — creases *and* view-dependent
 * silhouettes — into ONE fat-line draw call.
 *
 * Per-mesh `<Edges>` would be two draw calls per screw, so a fifty-item plan
 * would cost a thousand draws and still miss the silhouette of every cylinder.
 */
export function Outlined({
  children,
  color,
  width,
  opacity,
  disabled,
  contentVisible = true,
}: OutlinedProps) {
  const style = useRenderStyle()
  const contentRef = useRef<THREE.Group>(null)
  const viewDirection = useViewStore((state) => state.direction)

  const resolvedColor = color ?? style.outlineColor
  const resolvedWidth = width ?? style.outlineWidth
  const enabled = !disabled && style.showOutlines

  const material = useMemo(
    () =>
      getOutlineMaterial(
        () => createOutlineMaterial(resolvedColor, resolvedWidth),
        resolvedColor,
        resolvedWidth,
      ),
    [resolvedColor, resolvedWidth],
  )

  const line = useMemo(() => {
    const object = new LineSegments2(new LineSegmentsGeometry(), material)
    object.frustumCulled = false
    object.renderOrder = 2
    // Contours must never intercept pointer events; hit-testing uses the meshes.
    object.raycast = () => {}
    object.userData.outline = false
    return object
  }, [material])

  useLayoutEffect(() => {
    const alpha = opacity ?? 1
    material.opacity = alpha
    material.transparent = alpha < 1
  }, [material, opacity])

  // Deliberately dependency-free: it runs after every commit (including the ones
  // caused by a new view direction), and rebuilding is cheap because the face
  // adjacency table is cached per geometry.
  useLayoutEffect(() => {
    const root = contentRef.current
    if (!root || !enabled) return
    const points = collectContour(root, viewDirection, style.outlineThreshold, scratch)
    if (points.length === 0) {
      line.visible = false
      return
    }
    line.geometry.setPositions(new Float32Array(points))
    line.visible = true
  })

  return (
    <group>
      <group ref={contentRef} visible={contentVisible}>
        {children}
      </group>
      {enabled ? <primitive object={line} /> : null}
    </group>
  )
}
